/**
 * @file CouponsView.tsx
 *
 * Coupons tab screen. Fetches a server-paginated, searchable coupon list and
 * renders it as a table ({@link CouponsTable} / {@link Pagination}) with loading /
 * empty / no-results / error states. Editing navigates to the routed form; delete
 * is confirmed inline ({@link ConfirmModal}). Columns collapse on smaller
 * viewports via {@link useMediaQuery}, mirroring the Orders list.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { notifyError, notifySuccess } from '../feedback/toast';
import { useMediaQuery } from '../hooks/use-media-query';
import { useCouponsStore } from '../stores/coupons.store';
import { useSettingsStore } from '../stores/settings.store';
import { COUPONS_PER_PAGE } from '../api/coupons';
import type { ShopCoupon } from '../api/shop-types';
import { EmptyState } from '../components/EmptyState';
import { ConfirmModal } from '../components/ConfirmModal';
import { Pagination } from '../components/Pagination';
import { CouponsTable } from './CouponsTable';
import shared from '../shared.module.css';
import styles from './CouponsView.module.css';

/**
 * Renders the Coupons tab.
 *
 * @returns The coupons screen.
 */
export function CouponsView() {
  const intl = useIntl();
  const navigate = useNavigate();

  const coupons = useCouponsStore((s) => s.coupons);
  const couponsTotalPages = useCouponsStore((s) => s.couponsTotalPages);
  const loading = useCouponsStore((s) => s.loading);
  const error = useCouponsStore((s) => s.error);
  const fetchCoupons = useCouponsStore((s) => s.fetchCoupons);
  const deleteCoupon = useCouponsStore((s) => s.deleteCoupon);

  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState<ShopCoupon | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isMobile = useMediaQuery('Mobile');
  const isDesktop = useMediaQuery('Desktop');
  const showType = !isMobile;
  const showExpiry = !isMobile;
  const showUsage = isDesktop;

  useEffect(() => {
    fetchCoupons({ page, perPage: COUPONS_PER_PAGE, search: debouncedSearch });
  }, [fetchCoupons, page, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const refetch = (): void => {
    void fetchCoupons({ page, perPage: COUPONS_PER_PAGE, search: debouncedSearch });
  };

  const openCreate = (): void => {
    navigate('/coupons/new');
  };
  const openEdit = (coupon: ShopCoupon): void => {
    navigate(`/coupons/${coupon.id}/edit`);
  };
  const askDelete = (coupon: ShopCoupon): void => {
    setDeleteError(null);
    setConfirmDelete(coupon);
  };

  const handleDelete = async (id: string): Promise<void> => {
    setDeleting(id);
    setDeleteError(null);
    const ok = await deleteCoupon(id);
    setDeleting(null);
    if (!ok) {
      setDeleteError(intl.formatMessage(adminMessages.couponsDeleteError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastDeleted));
    setConfirmDelete(null);
    refetch();
  };

  const hasActiveFilters = debouncedSearch !== '';
  const totalPages = couponsTotalPages || 1;
  const safePage = Math.min(page, totalPages);

  if (loading && coupons.length === 0) {
    return (
      <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
        <Loader>
          <FormattedMessage {...adminMessages.couponsLoading} />
        </Loader>
      </div>
    );
  }

  if (error && coupons.length === 0) {
    return (
      <EmptyState
        testId="coupons-error"
        title={intl.formatMessage(adminMessages.couponsErrorTitle)}
        description={intl.formatMessage(adminMessages.couponsErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.couponsRetry),
          onClick: refetch,
        }}
      />
    );
  }

  return (
    <div className={`${styles.container} ${shared.outerContainer}`}>
      <div className={styles.toolbar}>
        <h2 className={`gv-heading-lg ${styles.title}`}>
          <FormattedMessage {...adminMessages.couponsTitle} />
        </h2>
        <button
          className="gv-button gv-button-primary"
          type="button"
          data-testid="coupons-add-button"
          onClick={openCreate}
        >
          <Icon name="add" size="small" />
          <FormattedMessage {...adminMessages.couponsAdd} />
        </button>
      </div>

      <div className="gv-flex gv-flex-col gv-gap-md">
        {(coupons.length > 0 || hasActiveFilters) && (
          <div className="gv-flex gv-justify-end">
            <div className="gv-mode-condensed">
              <div className={`gv-input gv-input-search gv-surface-bright ${styles.searchField}`}>
                <input
                  type="search"
                  data-testid="coupons-search"
                  value={search}
                  placeholder={intl.formatMessage(adminMessages.couponsSearchPlaceholder)}
                  aria-label={intl.formatMessage(adminMessages.couponsSearchLabel)}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <span className="gv-addon" aria-hidden="true">
                  <Icon name="search" size="small" />
                </span>
              </div>
            </div>
          </div>
        )}

        {coupons.length === 0 ? (
          hasActiveFilters ? (
            <EmptyState
              testId="coupons-no-results"
              title={intl.formatMessage(adminMessages.couponsNoResultsTitle)}
              description={intl.formatMessage(adminMessages.couponsNoResultsDescription)}
            />
          ) : (
            <EmptyState
              testId="coupons-empty"
              title={intl.formatMessage(adminMessages.couponsEmptyTitle)}
              description={intl.formatMessage(adminMessages.couponsEmptyDescription)}
              action={{
                label: intl.formatMessage(adminMessages.couponsAdd),
                onClick: openCreate,
              }}
              labelPrefix={<Icon name="add" size="small" />}
            />
          )
        ) : (
          <div className="gv-data-table">
            <CouponsTable
              coupons={coupons}
              currencySymbol={currencySymbol}
              showType={showType}
              showUsage={showUsage}
              showExpiry={showExpiry}
              onEdit={openEdit}
              onDelete={askDelete}
            />
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onPage={setPage}
              navLabel={intl.formatMessage(adminMessages.couponsTitle)}
              testIdPrefix="coupons-pagination"
            />
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.couponsDeleteTitle)}
          message={intl.formatMessage(adminMessages.couponsDeleteMessage, {
            code: confirmDelete.code,
          })}
          confirmLabel={intl.formatMessage(adminMessages.couponsDelete)}
          cancelLabel={intl.formatMessage(adminMessages.couponsCancel)}
          loadingLabel={intl.formatMessage(adminMessages.couponsDeleteLoading)}
          confirmVariant="destructive"
          loading={deleting === confirmDelete.id}
          errorMessage={deleteError ?? undefined}
          onPrimaryClick={() => void handleDelete(confirmDelete.id)}
          onSecondaryClick={() => setConfirmDelete(null)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
